import React from "react"
import { graphql, useFragment } from "react-relay"

import {
  SmartMatchingData,
  SmartMatchingDataPerson,
} from "~/SmartMatching/SmartMatchingContext"
import SmartMatchingScoreLabel from "~/SmartMatching/SmartMatchingScoreLabel"
import SkillsSection from "./SkillsSection"
import DefaultRoleSection from "./DefaultRoleSection"
import JobTitleSection from "./JobTitleSection"
import CustomSelectOptionsSection from "./CustomSelectOptionsSection"

import styles from "./MatchDetailTab.module.css"
import { PersonSidePanel_person$data } from "../__generated__/PersonSidePanel_person.graphql"
import { PlaceholderSidePanel_placeholder$data } from "~/Planner/PlaceholderSidePanel/__generated__/PlaceholderSidePanel_placeholder.graphql"
import { formatName } from "~/helpers/person"
import { MatchDetailTab_person$key } from "./__generated__/MatchDetailTab_person.graphql"

export type TargetPerson =
  | PersonSidePanel_person$data
  | PlaceholderSidePanel_placeholder$data

type Props = {
  smartMatchingData: SmartMatchingDataPerson
  embeddingFields: SmartMatchingData["embeddingFields"]
  sourcePerson: MatchDetailTab_person$key
  targetPerson: TargetPerson
}

const sourcePersonFragment = graphql`
  fragment MatchDetailTab_person on people {
    id
    first_name
    last_name
    ...DefaultRoleSection_person
    ...JobTitleSection_person
  }
`

const MatchDetailTab = ({
  smartMatchingData,
  embeddingFields,
  sourcePerson: sourcePersonQuery,
  targetPerson,
}: Props) => {
  const sourcePerson = useFragment(sourcePersonFragment, sourcePersonQuery)

  const formattedSourcePersonName = formatName(
    sourcePerson.first_name,
    sourcePerson.last_name,
  )

  return (
    <div className={styles.container}>
      <SmartMatchingScoreLabel
        score={smartMatchingData.totalScore}
        size="large"
        className={styles.overallScore}
      />
      <div className={styles.heading}>
        <div className={styles.headingText}>Match to:</div>
        <div title={formattedSourcePersonName}>{formattedSourcePersonName}</div>
      </div>
      <div className={styles.divider} />
      <div className={styles.sections}>
        <SkillsSection
          smartMatchingData={smartMatchingData}
          embeddingFields={embeddingFields}
        />

        <DefaultRoleSection
          smartMatchingData={smartMatchingData}
          embeddingFields={embeddingFields}
          targetPerson={targetPerson}
          source={sourcePerson}
        />

        <JobTitleSection
          smartMatchingData={smartMatchingData}
          embeddingFields={embeddingFields}
          targetPerson={targetPerson}
          source={sourcePerson}
        />

        <CustomSelectOptionsSection
          smartMatchingData={smartMatchingData}
          embeddingFields={embeddingFields}
        />
      </div>
    </div>
  )
}

export default MatchDetailTab
